import * as THREE from "three";

/**
 * Reads the RCA camera image and the live 3D scene back into 2D canvases, so the WebXR path feeds
 * the SAME 2D compositor as the gyro path. WebGL reads are bottom-up, so rows are flipped on the way
 * into the canvas.
 *
 * The camera feed is read by attaching its GL texture to a framebuffer and reading pixels directly
 * (Chrome's sanctioned raw-camera-access path) — shader-sampling the opaque camera texture returns
 * black on Android Chrome, so that route is deliberately avoided.
 */
export class WebXRCapturePass {
  /**
   * Read the RCA camera image straight off its GL texture via a framebuffer + readPixels — the path
   * Chrome's own raw-camera-access samples use. Sampling the opaque camera texture through a shader
   * returns black on some devices; a direct framebuffer read is the sanctioned CV path. Returns the
   * image plus the framebuffer status and peak channel so the caller can tell a working read from a
   * non-attachable (external/OES) or protected (zeroed) texture.
   */
  cameraTextureToCanvas(
    renderer: THREE.WebGLRenderer,
    glTexture: WebGLTexture,
    width: number,
    height: number,
  ): { canvas: HTMLCanvasElement; status: number; maxChannel: number } {
    const gl = renderer.getContext() as WebGL2RenderingContext;
    const framebuffer = gl.createFramebuffer();
    const previous = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, glTexture, 0);

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    const pixels = new Uint8Array(width * height * 4);
    let maxChannel = 0;
    if (status === gl.FRAMEBUFFER_COMPLETE) {
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i] > maxChannel) maxChannel = pixels[i];
        if (pixels[i + 1] > maxChannel) maxChannel = pixels[i + 1];
        if (pixels[i + 2] > maxChannel) maxChannel = pixels[i + 2];
      }
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, previous);
    gl.deleteFramebuffer(framebuffer);
    renderer.resetState(); // resync three's cached GL state after the raw framebuffer work

    return { canvas: pixelsToCanvas(pixels, width, height), status, maxChannel };
  }

  /** Render the live scene (transparent where empty) into a 2D canvas. */
  sceneToCanvas(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): HTMLCanvasElement {
    return this.renderToCanvas(renderer, scene, camera, width, height);
  }

  private renderToCanvas(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): HTMLCanvasElement {
    const target = new THREE.WebGLRenderTarget(width, height);
    // Offscreen targets are linear by default; encode to sRGB so the readback matches the on-screen
    // appearance (otherwise the composited scene comes out noticeably dark).
    target.texture.colorSpace = THREE.SRGBColorSpace;
    const previous = renderer.getRenderTarget();
    renderer.setRenderTarget(target);
    renderer.clear(true, true, true);
    renderer.render(scene, camera);

    const pixels = new Uint8Array(width * height * 4);
    renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
    renderer.setRenderTarget(previous);
    target.dispose();

    return pixelsToCanvas(pixels, width, height);
  }
}

function pixelsToCanvas(pixels: Uint8Array, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for capture readback");

  const image = ctx.createImageData(width, height);
  const rowBytes = width * 4;
  for (let y = 0; y < height; y++) {
    const sourceRow = (height - 1 - y) * rowBytes; // flip vertically
    image.data.set(pixels.subarray(sourceRow, sourceRow + rowBytes), y * rowBytes);
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}
